import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Upload, FileText, AlertCircle, Loader2, CheckCircle2, Cloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import PageHeader from "@/components/PageHeader";
import { getAccessToken, BACKEND_BASE } from "@/lib/api";

export default function Documents() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { success, intakeq }
  const [uploadError, setUploadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const fetchFiles = () => {
    API.get("/documents").then(({ data }) => { setFiles(data || []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { if (user) fetchFiles(); }, [user]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setUploadError("File size exceeds 25MB limit.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = getAccessToken();
      const resp = await fetch(`${BACKEND_BASE}/api/documents/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        body: formData,
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || `Upload failed (${resp.status})`);
      }

      const data = await resp.json();
      setUploadResult({
        success: true,
        intakeq: data.intakeq_uploaded,
      });
      fetchFiles();

      // Clear result after 5 seconds
      setTimeout(() => setUploadResult(null), 5000);
    } catch (err) {
      setUploadError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) return <div className="max-w-lg mx-auto px-4 py-6 flex items-center justify-center h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="max-w-lg mx-auto px-4 py-6 animate-fade-in" data-testid="documents-page">
      <PageHeader title="Documents" subtitle="Upload and manage your medical documents" />

      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{uploadError}</p>
        </div>
      )}

      {uploadResult?.success && (
        <div className={`${uploadResult.intakeq ? "bg-green-50 border-green-200" : "bg-blue-50 border-blue-200"} border rounded-lg p-3 mb-4 flex gap-2 animate-fade-in`}>
          <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${uploadResult.intakeq ? "text-green-600" : "text-blue-600"}`} />
          <p className={`text-sm ${uploadResult.intakeq ? "text-green-700" : "text-blue-700"}`}>
            {uploadResult.intakeq
              ? "File uploaded to your IntakeQ medical records!"
              : "File saved. Connect your IntakeQ account to sync files to your medical records."}
          </p>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-4 mb-6">
        <input ref={fileInputRef} type="file" onChange={handleFileSelect} disabled={uploading} className="hidden" />
        <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full gap-2 rounded-full" data-testid="upload-document-button">
          {uploading ? <><Loader2 className="w-4 h-4 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4" />Upload Document</>}
        </Button>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Supports images, PDFs, and documents (max 25MB). Files are uploaded to your IntakeQ record.
        </p>
      </div>

      {files.length === 0 ? (
        <EmptyState icon={FileText} title="No documents yet" description="Upload your insurance card, medical records, or other documents" />
      ) : (
        <div className="space-y-2">
          {files.map((file, idx) => (
            <div key={file.id || file.intakeq_file_id || idx} className="bg-card border border-border rounded-lg p-4 flex items-start gap-3" data-testid="document-item">
              <FileText className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{file.fileName}</p>
                <div className="flex items-center gap-2 mt-1">
                  {file.dateCreated && <p className="text-xs text-muted-foreground">{
                  typeof file.dateCreated === "number"
                    ? new Date(file.dateCreated / 1000 > 9999999999 ? file.dateCreated : file.dateCreated * 1000).toLocaleDateString()
                    : typeof file.dateCreated === "string" && file.dateCreated.includes("T")
                      ? new Date(file.dateCreated).toLocaleDateString()
                      : file.dateCreated
                }</p>}
                  {file.source === "intakeq" && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5 font-medium">
                      <Cloud className="w-2.5 h-2.5" /> IntakeQ
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
